import { XIcon } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './primitives';
import { cn } from '../cn';

/** Focusable-element selector used for the focus trap and initial focus. */
const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab within `container` and closes on Escape while `active`, restoring
 * focus to whatever held it when the overlay opened. Shared by the modal and
 * the drawer so both behave the same for keyboard users.
 *
 * @param active - Whether the overlay is open.
 * @param container - Ref to the overlay's root element.
 * @param onEscape - Called when Escape is pressed.
 * @param autoFocus - When true, focus moves to the first control on open.
 */
function useOverlayFocus(
  active: boolean,
  container: React.RefObject<HTMLElement | null>,
  onEscape: () => void,
  autoFocus = true,
): void {
  // Callers pass a fresh arrow for onEscape on every render. Keeping it in a
  // ref keeps it out of the effect's dependencies — otherwise the trap
  // re-ran on every keystroke, and each run moved focus back to the first
  // control, so typing into a dialog lost focus after one character.
  const escape = useRef(onEscape);
  escape.current = onEscape;

  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // React's own `autoFocus` has already focused the control the dialog
    // wants first (it runs on mount, before this effect); only when nothing
    // inside has focus does the trap pick the first control itself.
    if (autoFocus && !container.current?.contains(document.activeElement)) {
      container.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        escape.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = container.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, container, autoFocus]);
}

/** The visual weight of the confirm action. */
export type ConfirmTone = 'danger' | 'default';

export interface ConfirmDialogProps {
  /** Whether the dialog is mounted and visible. */
  readonly open: boolean;
  /** The headline — a short question, e.g. "Remove Priya's access?". */
  readonly title: string;
  /** The consequence, in plain words. What happens, and whether it can be undone. */
  readonly message: React.ReactNode;
  /** The confirm button label. Name the action ("Remove", "Archive"), not "OK". */
  readonly confirmLabel: string;
  /** The cancel label. Defaults to "Cancel". */
  readonly cancelLabel?: string;
  /** Danger tone paints the confirm button red; use it for destructive actions. */
  readonly tone?: ConfirmTone;
  /** Called when the person confirms. May be async; the dialog shows a pending state. */
  readonly onConfirm: () => void | Promise<void>;
  /** Called on cancel, backdrop click or Escape. */
  readonly onCancel: () => void;
  /** Disables the buttons and shows a working label while the action runs. */
  readonly pending?: boolean;
  /**
   * Blocks confirmation without disabling cancel — for when the dialog carries
   * a required field (a cancellation reason, say) that is not yet filled in.
   */
  readonly confirmDisabled?: boolean;
  /** An error from a failed confirm, shown in the dialog rather than dismissing it. */
  readonly error?: string | undefined;
}

/**
 * A focused confirmation modal for an action worth a second look.
 *
 * Every destructive or irreversible action routes through this rather than
 * firing on the first click: it states the consequence, names the action on its
 * button, and defaults focus to Cancel so muscle-memory Enter does not confirm.
 * Escape and a backdrop click both cancel; focus is trapped while open and
 * restored to the trigger on close.
 *
 * @param props - The copy, tone, and the confirm/cancel handlers.
 * @returns The dialog, portalled to the document body, or null when closed.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
  pending = false,
  confirmDisabled = false,
  error,
}: ConfirmDialogProps): React.ReactElement | null {
  const titleId = useId();
  const messageId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus defaults to Cancel (the first control), so a reflexive Enter cancels
  // rather than confirms; Escape cancels unless an action is mid-flight.
  useOverlayFocus(open, panelRef, () => {
    if (!pending) onCancel();
  });

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#04132980] p-4 backdrop-blur-[1px]"
      onMouseDown={(event) => {
        // Only a click that starts and ends on the backdrop cancels — a drag
        // that began on the text must not dismiss the dialog.
        if (event.target === event.currentTarget && !pending) onCancel();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className="w-full max-w-md rounded-portal-modal bg-white p-6 shadow-portal-modal"
      >
        <div className="flex items-start gap-4">
          {tone === 'danger' ? (
            <span
              aria-hidden="true"
              className="mt-0.5 grid size-10 shrink-0 place-items-center rounded-full bg-rose-100 text-[20px] font-bold leading-none text-rose-600"
            >
              !
            </span>
          ) : null}
          <div className="min-w-0">
            <h2 id={titleId} className="text-portal-card-title font-bold text-portal-ink">
              {title}
            </h2>
            <div id={messageId} className="mt-1.5 text-portal-small text-portal-muted">
              {message}
            </div>
          </div>
        </div>

        {error === undefined ? null : (
          <p role="alert" className="mt-4 rounded-portal-card bg-rose-50 p-3 text-portal-small text-rose-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={() => void onConfirm()}
            disabled={pending || confirmDisabled}
            className={cn(
              tone === 'danger' &&
                'bg-rose-600 shadow-none hover:bg-rose-700 focus-visible:outline-rose-300',
            )}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export interface DrawerProps {
  /** Whether the drawer is mounted and visible. */
  readonly open: boolean;
  /** The heading shown in the drawer's header. */
  readonly title: string;
  /** Optional subheading under the title. */
  readonly subtitle?: string;
  /** Called on close — the ✕, a backdrop click, or Escape. */
  readonly onClose: () => void;
  /** The drawer body. Scrolls when taller than the viewport. */
  readonly children: React.ReactNode;
  /** Optional pinned footer, e.g. Save / Cancel actions. */
  readonly footer?: React.ReactNode;
  /**
   * `wide` for a form that previews what it makes (a portal banner), where the
   * default width would squeeze the preview into a shape no client sees.
   */
  readonly size?: 'default' | 'wide';
}

/**
 * A right-hand slide-over panel for editing a record in place, in the manner of
 * HubSpot's record forms — you keep the list behind it for context rather than
 * navigating away. Accessible like the modal: role=dialog, focus trapped,
 * Escape and backdrop close, focus restored to the trigger.
 *
 * @param props - Open state, header copy, the body, and an optional footer.
 * @returns The drawer, portalled to the body, or null when closed.
 */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'default',
}: DrawerProps): React.ReactElement | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useOverlayFocus(open, panelRef, onClose);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[#04132966]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'flex h-full w-full animate-portal-slide-in flex-col bg-white shadow-portal-modal',
          size === 'wide' ? 'max-w-2xl' : 'max-w-md',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-portal-line px-6 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-portal-card-title font-bold text-portal-ink">
              {title}
            </h2>
            {subtitle === undefined ? null : (
              <p className="mt-0.5 text-portal-micro text-portal-muted">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            // The ::after pad makes a 44px tap target around the 24px button without moving it.
            className="relative -mr-1 shrink-0 rounded p-1 text-[20px] leading-none text-portal-muted after:absolute after:-inset-2.5 after:content-[''] hover:text-portal-ink"
          >
            <XIcon aria-hidden className="size-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer === undefined ? null : (
          <footer className="border-t border-portal-line px-6 py-4">{footer}</footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export interface ModalProps {
  /** Whether the modal is mounted and visible. */
  readonly open: boolean;
  /** The headline. */
  readonly title: string;
  /** Optional line under the title. */
  readonly subtitle?: string;
  /** The body. */
  readonly children: React.ReactNode;
  /** The action row, right-aligned under the body. */
  readonly actions?: React.ReactNode;
  /** Called on Escape, a backdrop click, or the ✕. */
  readonly onClose: () => void;
  /**
   * False keeps a click on the backdrop from closing it (Escape and the ✕
   * still do), for something a stray click shouldn't lose, like a playing video.
   */
  readonly closeOnBackdrop?: boolean;
  /**
   * Hides the ✕ and stops Escape/backdrop from closing, for a modal the person
   * must answer with one of its own actions. Use sparingly — a modal with no
   * way out is a trap, so `actions` must then contain a dismissal.
   */
  readonly dismissible?: boolean;
  /** Widens the panel past the default 28rem, e.g. `max-w-lg`. */
  readonly className?: string;
  /**
   * Below 768px, fill the screen instead of floating as a card, with the
   * actions pinned to the bottom. For a modal with real work in it (a search
   * and a list to pick from), where a card on a phone leaves no room.
   */
  readonly phoneFullScreen?: boolean;
}

/**
 * A general-purpose modal for content that is neither a yes/no confirmation
 * ({@link ConfirmDialog}) nor a record editor ({@link Drawer}) — an
 * announcement, an onboarding note, a short explanation.
 *
 * Accessible like the others: role=dialog, focus trapped while open, focus
 * restored to the trigger on close.
 *
 * @param props - Open state, copy, body, actions and the close handler.
 * @returns The modal, portalled to the body, or null when closed.
 */
export function Modal({
  open,
  title,
  subtitle,
  children,
  actions,
  onClose,
  dismissible = true,
  closeOnBackdrop = true,
  className,
  phoneFullScreen = false,
}: ModalProps): React.ReactElement | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useOverlayFocus(open, panelRef, () => {
    if (dismissible) onClose();
  });

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-[#04132980] p-4 backdrop-blur-[1px]',
        phoneFullScreen && 'max-md:p-0',
      )}
      onMouseDown={(event) => {
        // Only a click that starts and ends on the backdrop closes — a drag
        // that began on the text must not dismiss the modal.
        if (event.target === event.currentTarget && dismissible && closeOnBackdrop) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'w-full max-w-md rounded-portal-modal bg-white p-6 shadow-portal-modal',
          className,
          phoneFullScreen &&
            'max-md:flex max-md:h-dvh max-md:max-w-none max-md:flex-col max-md:overflow-y-auto max-md:rounded-none max-md:p-4 max-md:pb-[max(1rem,env(safe-area-inset-bottom))]',
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-portal-card-title font-bold text-portal-ink">
              {title}
            </h2>
            {subtitle === undefined ? null : (
              <p className="mt-0.5 text-portal-small text-portal-muted">{subtitle}</p>
            )}
          </div>
          {dismissible ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={cn(
                '-mt-1 -mr-1 shrink-0 rounded p-1 text-[20px] leading-none text-portal-muted hover:text-portal-ink',
                phoneFullScreen && 'max-md:-mt-2 max-md:-mr-2 max-md:grid max-md:size-10 max-md:place-items-center',
              )}
            >
              <XIcon aria-hidden className="size-4" />
            </button>
          ) : null}
        </div>

        <div className={cn('mt-4 text-portal-small text-portal-muted', phoneFullScreen && 'max-md:flex-1')}>
          {children}
        </div>

        {actions === undefined ? null : (
          <div className="mt-6 flex flex-wrap justify-end gap-2">{actions}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
